import {
  Component,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CdkOverlayOrigin } from "@angular/cdk/overlay";
import { CONNECTED_OVERLAY_POSITIONS_BELOW } from "@vault/web-vault/app/shared/utils/overlay-positions";
import { FormControl } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { DropdownDirective } from "@vault/web-vault/app/directives/dropdown.directive";
import { InputDirective } from "@vault/web-vault/app/directives/input.directive";
import { FLAG_SYMBOL_IDS } from "./flag-symbol-ids";

@Component({
  selector: "app-dropdown-search",
  templateUrl: "./dropdown-search.component.html",
})
export class DropdownSearchComponent implements OnInit, OnDestroy, OnChanges {
  @Input() label: string;
  @Input() isRequired: boolean = false;
  @Input() placeholder: string = "";
  @Input() options: string[] = [];
  taggedOptions = input<string[]>([]);
  @Input() isEnabled: boolean = true;
  @Input() preSelected: string = "";
  @Input() isPicker: boolean = false; // If true, user can click and emit options without closing dropdown
  @Input() manualTouched: boolean = false; // Allow parent to mark as touched
  @Input() allowCreate: boolean = false;
  @Output() optionSelected = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();
  @Output() createRequested = new EventEmitter<void>();

  // Optional leading dropdown rendered as a prefix segment inside the search bar.
  // When enabled, the parent can pre-filter the searchable options by whatever the
  // leading dropdown selects (e.g. pick a country to scope the institution list).
  @Input() leadingDropdownEnabled: boolean = false;
  @Input() leadingOptions: string[] = [];
  @Input() leadingPlaceholder: string = "";
  @Input() leadingSelected: string = "";
  // Maps a leading option's label to its ISO 3166-1 alpha-2 code (e.g. country flag), keyed
  // by the option string. Optional — only used when the leading dropdown represents a country.
  @Input() leadingOptionCodes: Record<string, string> = {};
  @Output() leadingOptionSelected = new EventEmitter<string>();

  // Local open/close state for the leading dropdown (kept separate from the search dropdown).
  protected leadingExpanded = false;

  // Both panels render in a CDK connected overlay so they escape any clipping/scrolling
  // ancestor (e.g. the modal-body that wraps this component in the account flow).
  // Prefer opening below the trigger, flip above when there isn't room.
  protected readonly overlayPositions = CONNECTED_OVERLAY_POSITIONS_BELOW;

  // Memoized so the template binding doesn't re-sort (or re-resolve each option's flag) on
  // every change-detection tick; recomputed only when leadingOptions/leadingOptionCodes
  // change (see ngOnChanges).
  leadingOptionsWithFlags: { label: string; flag: string }[] = [];
  // Flag for the trigger button's current selection — same precompute-on-change reasoning.
  selectedFlag: string = "";

  inputControl: FormControl = new FormControl("");
  filteredOptions: string[] = [];
  private destroy$ = new Subject<void>();
  private searchFilterReference: string = ""; // Track the current search filter

  protected baseInput = new InputDirective();
  protected dropdown = new DropdownDirective();

  ngOnInit(): void {
    this.options = [...this.options].sort((a, b) => a.localeCompare(b));
    this.inputControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.searchFilterReference = value || "";
      this.filteredOptions = this.options.filter((option) =>
        option.toLowerCase().includes(value?.toLowerCase()),
      );
    });
    if (!this.preSelected) {
      this.filteredOptions = [...this.options];
    } else {
      this.filteredOptions = [this.preSelected];
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEnabled) {
      if (this.isEnabled) {
        this.inputControl.enable({ emitEvent: false });
        if (!changes.isEnabled.firstChange) {
          this.dropdown.isTouched = false;
        }
      } else {
        this.inputControl.disable({ emitEvent: false });
        this.dropdown.isExpanded = false;
        this.leadingExpanded = false;
      }
    }

    if (changes.preSelected && this.inputControl) {
      this.inputControl.setValue(changes.preSelected.currentValue);
    }

    if (changes.options) {
      this.options = [...changes.options.currentValue].sort((a: string, b: string) =>
        a.localeCompare(b),
      );
      if (this.searchFilterReference) {
        this.filteredOptions = this.options.filter((option) =>
          option.toLowerCase().includes(this.searchFilterReference.toLowerCase()),
        );
      } else {
        this.filteredOptions = [...this.options];
      }
    }

    if (changes.leadingOptions || changes.leadingOptionCodes) {
      this.leadingOptionsWithFlags = [...this.leadingOptions]
        .sort((a, b) => {
          // "Other" (e.g. the synthetic catch-all country) always sorts last, not alphabetically.
          if (a === "Other") {
            return 1;
          }
          if (b === "Other") {
            return -1;
          }
          return a.localeCompare(b);
        })
        .map((label) => ({ label, flag: this.flagSymbolId(label) }));
    }

    if (changes.leadingSelected || changes.leadingOptionCodes) {
      this.selectedFlag = this.flagSymbolId(this.leadingSelected);
    }

    if (changes.manualTouched && changes.manualTouched.currentValue) {
      this.dropdown.isTouched = true;
    }
  }

  toggleLeading(): void {
    if (!this.isEnabled) {
      return;
    }
    this.leadingExpanded = !this.leadingExpanded;
    // Don't let both panels stack on top of each other.
    if (this.leadingExpanded) {
      this.dropdown.isExpanded = false;
    }
  }

  selectLeadingOption(option: string): void {
    this.leadingExpanded = false;
    if (option !== this.leadingSelected) {
      this.leadingOptionSelected.emit(option);
    }
  }

  // Close on a click outside the overlay panel, but ignore clicks on the trigger itself
  // so the trigger's own (click) handler keeps full control of the toggle.
  onLeadingOverlayOutsideClick(event: MouseEvent, origin: CdkOverlayOrigin): void {
    if (!origin.elementRef.nativeElement.contains(event.target as Node)) {
      this.leadingExpanded = false;
    }
  }

  onSearchOverlayOutsideClick(event: MouseEvent, trigger: HTMLElement): void {
    if (!trigger.contains(event.target as Node)) {
      this.dropdown.onClickOutside();
    }
  }

  toggleExpand(): void {
    // todo: might need to look back, closing it now rely on on click outside
    if (!this.dropdown.isExpanded) {
      this.dropdown.toggleExpand();
    }
  }

  onWrapperClick(inputElement: HTMLInputElement): void {
    if (!this.isEnabled) {
      return;
    }
    // Focus the input to make the entire wrapper area clickable
    inputElement.focus();

    // Opening the search list should close the leading panel so they don't overlap.
    this.leadingExpanded = false;

    // Handle dropdown expansion logic
    if (!this.baseInput.isFocused || !this.dropdown.isExpanded) {
      this.toggleExpand();
    }
  }

  selectOption(option: string): void {
    if (!this.isPicker) {
      this.dropdown.selectOption(option);
      this.inputControl.setValue(option);
    }

    this.optionSelected.emit(option);
  }

  /** Todo @sinan : Fix the method, need to return the tag based on the value and also make it be
   *  i18n keep in mind that one list can have multiple tags one item in the list may have multiple
   *  tags as well*/
  getTag(value: string) {
    return "Manually Added";
  }

  // todo: whats this for
  getInputSize(): number {
    const val = this.inputControl?.value || "";
    const base = this.placeholder?.length || 8;
    return val.length > 0 ? val.length + 2 : base;
  }

  isTagged = (value: string): boolean => {
    return this.taggedOptions()?.includes(value);
  };

  // Resolves a leading option's label to a sprite.svg <symbol> id, or "" when the label has
  // no code (leadingOptionCodes[option] is unset) or the code has no bundled flag artwork
  // (FLAG_SYMBOL_IDS) — either way the caller renders no flag rather than a dangling <use>.
  flagSymbolId(option: string): string {
    const code = this.leadingOptionCodes[option]?.toLowerCase();
    return code && FLAG_SYMBOL_IDS.has(code) ? code : "";
  }

  isInvalid(): boolean {
    return (
      this.isEnabled &&
      this.isRequired &&
      this.dropdown.isTouched &&
      (!this.inputControl.value || !this.options.includes(this.inputControl.value)) &&
      !this.dropdown.isExpanded
    );
  }

  onInput(): void {
    this.baseInput.onFocus();
    // Emptying the field by hand (backspace) counts as clearing the selection,
    // same as the clear (×) button. Only fires on real user input, not programmatic
    // value changes (e.g. preSelected), so it can't loop.
    if (!this.inputControl.value) {
      this.cleared.emit();
    }
  }

  onBlur(): void {
    this.baseInput.onBlur();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearInput() {
    this.inputControl.setValue("");
    this.cleared.emit();
  }

  trackByOption(index: number, option: string): string {
    return option;
  }
}
